import { useEffect, useState } from 'react'
import { MailWarning } from 'lucide-react'
import {
  getThisPollEmailRuleForwards,
  type ThisPollEmailRuleForward,
} from '../lib/api'

// DW-ITEM21-THIS-POLL-FE — the only window onto this-poll
// email_rule_forward_surfaced rows. Mounted on the analyst Alerts page
// (audience 'analyst', the default) and on the client home
// (audience 'client'). The audience switches ONLY the disarmed-copy
// sentence — headings, aria-labels, blind-on-empty and blind-on-failure
// behaviour are identical on both surfaces.
//
// Doctrine (mirrors the backend endpoint's contract):
// - Only rows from GET /alerts/this-poll/email-rule-forwards appear here.
//   Historical store rows (aged last_changed_at, lifetime counts) never do.
// - [] means BLIND: render NOTHING. No empty success card, no "no forwarding
//   rules", no all-clear copy — absence of evidence is not a verdict, and this
//   section must not paint one. A failed fetch also renders nothing: with no
//   rows in hand the section has no claim to make in either direction.
// - Response is DISARMED: rule id + new target addresses are evidence for the
//   analyst's eyes. No Approve/Reject hangs off this surface.
export function ThisPollForwardEvidence({
  audience = 'analyst',
}: {
  audience?: 'analyst' | 'client'
}) {
  const [rows, setRows] = useState<ThisPollEmailRuleForward[]>([])

  useEffect(() => {
    let cancelled = false
    getThisPollEmailRuleForwards()
      .then((list) => {
        if (!cancelled) setRows(list)
      })
      .catch(() => {
        // Stay BLIND: no rows, no claims, no section.
      })
    return () => {
      cancelled = true
    }
  }, [])

  if (rows.length === 0) return null

  return (
    <section
      aria-label="This-poll email rule forwards"
      className="mb-6 bg-white rounded-xl border border-warning/40 p-4"
    >
      <div className="flex items-center gap-2 mb-3">
        <MailWarning className="w-4 h-4 text-warning" aria-hidden="true" />
        <h2 className="text-sm font-semibold text-navy">
          Email rule forwards surfaced this poll
        </h2>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        Evidence from the current poll only — not a verdict.{' '}
        {audience === 'client'
          ? 'The team decides next steps. This surface executes nothing.'
          : 'The analyst makes the call; nothing executes from this section.'}
      </p>
      <ul className="space-y-3">
        {rows.map((row) => (
          <li
            key={row.signal_id}
            className="rounded-lg border border-gray-200 p-3 text-sm text-navy"
          >
            <ul className="space-y-2">
              {(row.evidence ?? []).map((item, i) => (
                <li key={`${row.signal_id}-${item.rule_id ?? i}`}>
                  <span className="font-medium">
                    Rule {item.rule_id ?? '(id unknown)'}
                  </span>
                  <span className="text-gray-500"> → new targets: </span>
                  <span>
                    {(item.new_targets ?? [])
                      .map((t) => t.address)
                      .filter(Boolean)
                      .join(', ') || '(address not on payload)'}
                  </span>
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </section>
  )
}
